import { TimeInForceType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/TimeInForceTypeProto';
import Divider from '@/components/common/Divider';
import Tooltip from '@/components/common/Tooltip';
import FormBody from '@/components/form/components/FormBody';
import FormCurrency from '@/components/form/components/FormCurrency';
import FormFooter from '@/components/form/components/FormFooter';
import FormOrderTypeSwitch from '@/components/form/components/FormOrderTypeSwitch';
import FormSubmit from '@/components/form/components/FormSubmit';
import Totals from '@/components/form/components/Totals';
import { LimitOrderFormInput, LimitOrderFormValues, limitOrderSchema } from '@/components/form/schema/limitOrderSchema';
import ExecutionInputController from '@/components/inputs/ExecutionInputController';
import RHFNumberInput from '@/components/inputs/RHFNumberInput';
import Select from '@/components/inputs/Select';
import SlippageInput from '@/components/inputs/SlippageInput';
import { addMarketFull } from '@/services/MarketService';
import { submitLimitOrder } from '@/services/OrderService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDebouncedValue } from '@/utils/hooks/useDebouncedValue';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import useLimitOrder from '@/utils/hooks/useLimitOrder';
import { UseOrderExecutionTraderReturn } from '@/utils/hooks/useOrderExecutionTrader';
import useScreenSizes from '@/utils/hooks/useScreenSizes';
import useTotals from '@/utils/hooks/useTotals';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import { useEffect } from 'react';
import { isTablet } from 'react-device-detect';
import { FormProvider, useForm } from 'react-hook-form';

const durationOptions: { label: keyof typeof TimeInForceType; value: TimeInForceType }[] = [
    { label: 'DAY', value: TimeInForceType.DAY },
    { label: 'IOC', value: TimeInForceType.IOC },
    { label: 'FOK', value: TimeInForceType.FOK },
    { label: 'GTC', value: TimeInForceType.GTC }
];

const defaultValues: Partial<LimitOrderFormInput> = {
    limitPrice: null,
    quantity: null,
    duration: durationOptions[0],
    slippage: '0.00000'
};

interface LimitOrderFormProps extends UseOrderExecutionTraderReturn {
    onClose?: () => void;
    closeOnSubmit?: boolean;
}

function LimitOrderForm(props: LimitOrderFormProps) {
    const { onClose, closeOnSubmit = false, ...orderExecutionProps } = props;
    const {
        ccy1,
        ccy2,
        sideState,
        currencyState,
        orderBook,
        instrument,
        activeCurrencyConfig,
        activeTicker,
        minOrderSize,
        orderDecimals,
        isCcy2Order
    } = orderExecutionProps;

    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const screen = useScreenSizes();

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;

    const { asks, bids } = orderBook;
    const { pip_size, defaultSlippage } = instrument;
    const { increments } = activeCurrencyConfig;

    const form = useForm<LimitOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            limitOrderSchema({
                min_order_size: minOrderSize,
                order_decimals: orderDecimals,
                currencyOut: currency,
                currencyPair: activeTicker.show
            })
        )
    });

    const totals = useTotals({
        form,
        activePair: activeTicker,
        side,
        ccy1,
        ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        watch,
        register,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const onSubmit = async (data: LimitOrderFormInput, e) => {
        const dataValidated = data as LimitOrderFormValues;
        const slippage = dataValidated.slippage
            ? BigNumber(dataValidated.slippage)
                  .dividedBy(Math.pow(10, pip_size || 0))
                  .toString()
            : '0';
        if (dataValidated.quantity && credentials) {
            if (closeOnSubmit) onClose?.();
            await submitLimitOrder(
                {
                    securityId: activeTicker.celer,
                    currencyOut: currency,
                    quantity: dataValidated.quantity,
                    limitPrice: dataValidated.limitPrice,
                    timeInForce: dataValidated.duration.value as TimeInForceType,
                    slippage,
                    side
                },
                credentials,
                orderBook.settlementDate,
                currentAccount
            );
        }
    };

    const onError = (errors) => console.error(errors);

    const { limitPriceTooltip, isRestingLimitOrder, resetDefault } = useLimitOrder(form, props, '');

    useDidUpdate(() => resetDefault(), [currency]);

    useEffect(() => {
        resetDefault();
    }, [activeTicker.celer]);

    const [quantity] = watch(['quantity']);
    const [debouncedQuantity] = useDebouncedValue(quantity, 300);

    useDidUpdate(() => {
        if (credentials) {
            if (debouncedQuantity && debouncedQuantity > 0) {
                // console.log('addMarketFull due to quantity', market.celer);
                addMarketFull(activeTicker.celer);
            }
        }
    }, [debouncedQuantity]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="relative flex flex-col h-full w-full text-brand-text">
                <FormBody side={side} mode="Modal">
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    <FormCurrency {...props} mode="Trader" currency={currency} setCurrency={setCurrency} />
                    <div>
                        <Tooltip content={limitPriceTooltip || ''} className="-top-16 right-0 w-2/3">
                            <RHFNumberInput
                                label={`${lang.orderForm.limitPrice} *`}
                                placeholder={lang.orderForm.limitPrice}
                                {...register('limitPrice')}
                                disabled={!isRestingLimitOrder}
                                className={{
                                    'text-brand-red': limitPriceTooltip,
                                    'text-brand-text': !limitPriceTooltip
                                }}
                                step={BigNumber(1)
                                    .dividedBy(Math.pow(10, pip_size || 0))
                                    .toNumber()}
                                currency={activeCurrencyConfig.show}
                            />
                        </Tooltip>
                        {(screen.mobile || screen.tablet || isTablet) && (
                            <span className="text-brand-red italic">{limitPriceTooltip}</span>
                        )}
                    </div>
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    <RHFNumberInput
                        {...register('quantity')}
                        label={`${lang.orderForm.quantity} *`}
                        placeholder={lang.orderForm.quantity}
                        min={minOrderSize}
                        step={increments}
                        currency={activeCurrencyConfig.show}
                    />
                    <SlippageInput
                        {...register('slippage')}
                        label={lang.orderForm.slippage}
                        placeholder={lang.orderForm.slippage}
                        disabled={isRestingLimitOrder}
                        defaultSlippage={defaultSlippage}
                        step={0.1}
                    />
                    <Totals {...totals} isTrader />
                </FormBody>
                <Divider />
                <FormFooter mode="Modal">
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit}
                        noSettlementDate={!orderBook.settlementDate}
                        className="left-6"
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default LimitOrderForm;
